import { createContext, ReactNode, useReducer } from 'react';
import { getLocalStorageValue, setLocalStorageValue } from '~/hooks/use-local-storage';
import { uid } from '../helpers/uid';
import { onlyUnique } from '../helpers/ArrayHelper';
import { LocalStorageKeys } from '../consts/LocalStorageKeys';

export const Achievements = [
  'SmartSendSetupAccountScreen',
  'SmartSendActionAccountLinked',
  'SmartSendActionCsvUploaded',

  'SmartSendSetupCollectionAddressScreen',
  'SmartSendActionCollectionAddressAdded',

  'SmartSendSetupRuleScreen',
  'SmartSendActionRuleAdded',

  'SmartSendSetupCompleteScreen',
  'SmartSendSignup',

  'SmartSendActionFirstParcelSent'
] as const;

export type AchievementType = (typeof Achievements)[number];

export type Achievement = { key: AchievementType; value: boolean; hide: boolean };

export interface ProfileContextStateModel {
  achievements: Achievement[];
  override: boolean;
  userSkipped: boolean;
  cacheKey?: string;
  loading?: boolean;
  outstandingActions: string[];
  signupIntegrations?: string[];
  numberOfParcelsPerWeek?: number;
  created: string;
  meta: { [key: string]: any };
}

export interface IProfileContextAction {
  type:
    | 'USER-HIDE-ACHIEVEMENTS'
    | 'USER-MARK-AS-DONE'
    | 'USER-SKIP-ONBOARDING'
    | 'USER-SET-ACHIEVEMENTS'
    | 'USER-SET-POST-REGISTRATION-FORM-DATA'
    | 'TESTING-SET-STATE'
    | 'END-TEST-SESSION'
    | 'USER-ADD-ACTIONS-OUTSTANDING'
    | 'USER-REMOVE-ACTIONS-OUTSTANDING'
    | 'USER-SET-ACTIONS-OUTSTANDING'
    | 'RESET';
  payload?: any;
}

const UpdateStorage = (state: ProfileContextStateModel) => {
  setLocalStorageValue(LocalStorageKeys.SmartSendSetup, state);
};

export function ConcernsContextReducer(state: ProfileContextStateModel, action: IProfileContextAction) {
  switch (action.type) {
    case 'USER-SKIP-ONBOARDING': {
      const updatedState = { ...state, userSkipped: true };
      UpdateStorage(updatedState);
      return updatedState;
    }

    case 'USER-HIDE-ACHIEVEMENTS': {
      const payload: AchievementType[] = action.payload;
      const updatedState = { ...state };
      updatedState.achievements.forEach((achievement) => {
        if (payload.includes(achievement.key)) {
          achievement.hide = true;
        }
      });
      UpdateStorage(updatedState);
      return updatedState;
    }

    case 'RESET': {
      const updatedState = { ...initialState, cacheKey: uid() };
      UpdateStorage(updatedState);
      return updatedState;
    }

    case 'END-TEST-SESSION': {
      const updatedState: ProfileContextStateModel = {
        ...initialState,
        cacheKey: uid()
      };
      UpdateStorage(updatedState);
      return updatedState;
    }

    case 'USER-MARK-AS-DONE': {
      const payload: {
        achievements: AchievementType[];
        outstandingActionsToAdd: string[];
        outstandingActionsToRemove: string[];
        meta: { [key: string]: any };
      } = action.payload;

      const updatedState = { ...state, meta: { ...state.meta, ...payload.meta } };

      if (payload.outstandingActionsToAdd?.length) {
        updatedState.outstandingActions = [
          ...(updatedState.outstandingActions || []),
          ...payload.outstandingActionsToAdd
        ].filter(onlyUnique);
      }

      if (payload.outstandingActionsToRemove?.length) {
        updatedState.outstandingActions = (updatedState.outstandingActions || []).filter(
          (x) => !payload.outstandingActionsToRemove.includes(x)
        );
      }

      payload.achievements.forEach((key) => {
        const achievement = updatedState.achievements.find((a) => a.key === key);
        if (achievement) {
          achievement.value = true;
        } else {
          updatedState.achievements.push({ key: key, value: true, hide: false });
        }
      });

      UpdateStorage(updatedState);
      return updatedState;
    }

    case 'USER-ADD-ACTIONS-OUTSTANDING': {
      const payload: string[] = action.payload;
      const updatedState = { ...state };
      updatedState.outstandingActions = [...(updatedState.outstandingActions || []), ...payload].filter(onlyUnique);
      UpdateStorage(updatedState);
      return updatedState;
    }

    case 'USER-REMOVE-ACTIONS-OUTSTANDING': {
      const payload: string[] = action.payload;
      const updatedState = { ...state };
      updatedState.outstandingActions = (updatedState.outstandingActions || []).filter((x) => !payload.includes(x));
      UpdateStorage(updatedState);
      return updatedState;
    }

    case 'USER-SET-ACTIONS-OUTSTANDING': {
      const payload: string[] = action.payload || [];
      const updatedState: ProfileContextStateModel = { ...state, outstandingActions: payload };
      UpdateStorage(updatedState);
      return updatedState;
    }

    case 'USER-SET-POST-REGISTRATION-FORM-DATA': {
      const payload: { numberOfParcels: number; integrations: string[] } = action.payload;
      if (payload) {
        const updatedState: ProfileContextStateModel = {
          ...state,
          numberOfParcelsPerWeek: payload.numberOfParcels,
          signupIntegrations: payload.integrations
        };
        UpdateStorage(updatedState);
        return updatedState;
      } else {
        return { ...state };
      }
    }

    case 'USER-SET-ACHIEVEMENTS': {
      const payload: {
        achievements: {
          [key: string]: boolean;
        };
      } = action.payload;

      const updatedState = { ...state };
      for (const key in payload.achievements) {
        const achievement = updatedState.achievements.find((a) => a.key === key);
        if (achievement) {
          achievement.value = payload.achievements[key];
        } else {
          updatedState.achievements.push({
            key: key as AchievementType,
            value: payload.achievements[key],
            hide: false
          });
        }
      }
      updatedState.loading = false;
      UpdateStorage(updatedState);
      return updatedState;
    }

    case 'TESTING-SET-STATE': {
      const payload = action.payload as {
        achievements: AchievementType[];
      };
      const updatedState: ProfileContextStateModel = {
        ...state,
        userSkipped: false,
        loading: false,
        override: true,
        outstandingActions: []
      };
      updatedState.achievements.forEach((a) => {
        a.value = payload.achievements.includes(a.key);
        a.hide = false;
      });
      UpdateStorage(updatedState);
      return updatedState;
    }

    default: {
      const updatedState = { ...state };
      UpdateStorage(updatedState);
      return updatedState;
    }
  }
}

const initialState: ProfileContextStateModel = {
  achievements: [],
  override: false,
  userSkipped: false,
  loading: true,
  outstandingActions: [],
  signupIntegrations: undefined,
  numberOfParcelsPerWeek: undefined,
  created: new Date().toISOString(),
  meta: {}
};

const ProfileContext = createContext<{
  state: typeof initialState;
  dispatch: (action: IProfileContextAction) => void;
}>({
  state: initialState,
  dispatch: () => {}
});

const ProfileContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(ConcernsContextReducer, {
    ...(getLocalStorageValue(LocalStorageKeys.SmartSendSetup) ?? { ...initialState })
  });

  const value = { state, dispatch };

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

const ProfileConsumer = ProfileContext.Consumer;

export { ProfileContext, ProfileContextProvider, ProfileConsumer };
